import { CalendarDays, Clock3, Image, PanelsTopLeft, Play, Plus, RefreshCw, Settings2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimeWall, EmptyWall, LoadingWall, RequestErrorWall } from '../components/content-wall';
import { Button } from '../components/button';
import { CollectionTimeline } from '../components/collection-timeline';
import { MediaSourceDialog } from '../components/media-source-dialog';
import { ProgressBar, progressDetails } from '../components/progress-bar';
import { Switch } from '../components/switch';
import { TabBar, type TabBarItem } from '../components/tab-bar';
import { useApp } from '../components/app-context';
import { t } from '../i18n';
import {
  $currentUser,
  $homeLayout,
  $japaneseAnimeOnly,
  $scheduleFollowingOnly,
  $showLateNightAnime,
  $showNsfw,
  setScheduleFollowingOnly,
} from '../store/app.store';
import { useStore } from '../hooks/use-store';
import { loadPopularSubjects } from '../services/popular-subjects';
import type {
  AnimeSubject,
  BgmCurrentUser,
  CalendarGroup,
  Episode,
  SubjectBroadcast,
  SubjectCollectionPage,
  SubjectPage,
} from '../types/bgm';
import type { MediaResourcePlayResult, WatchLaterPage, WatchProgressInfo } from '../types/page';
import { formatDateTime } from '../utils/date';
import { safeUrl } from '../utils/url';

export const HomePage = () => {
  const { navigate } = useApp();
  const layout = useStore($homeLayout);
  const sections = {
    popular: <PopularSection key="popular" />,
    today: <TodayUpdatesSection key="today" />,
    collectionRecords: <CollectionTimeline key="collectionRecords" variant="home" />,
    continue: <ContinueWatchingSection key="continue" />,
    watchLater: <WatchLaterSection key="watchLater" />,
    collections: <CollectionSection key="collections" />,
    random: <RandomSection key="random" />,
    recommendations: <RecommendationSection key="recommendations" />,
  };
  return (
    <section>
      <div className="hero-copy compact-hero">
        <p className="eyebrow">DISCOVERY</p>
        <h1>{t('homeTitle')}</h1>
      </div>
      {layout.visible.length > 0 ? (
        layout.visible.map((section) => sections[section])
      ) : (
        <div className="wall-message home-empty-state flex min-h-65 flex-col items-center justify-center gap-3.5 p-7 text-center">
          <PanelsTopLeft />
          <div>
            <strong>{t('homeEmpty')}</strong>
            <p>{t('homeEmptyDescription')}</p>
          </div>
          <Button onClick={() => navigate('/settings')}>
            <Settings2 />
            <span>{t('homeEmptyAction')}</span>
          </Button>
        </div>
      )}
    </section>
  );
};

interface ContinueWatchingItem {
  episode: Episode;
  progress: WatchProgressInfo;
  subject: AnimeSubject;
}

const ContinueWatchingSection = () => {
  const { api, bgm, locale, navigate, showToast } = useApp();
  const [items, setItems] = useState<ContinueWatchingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retryRevision, setRetryRevision] = useState(0);
  const [deletingEpisodeId, setDeletingEpisodeId] = useState<number>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void api<WatchProgressInfo[]>('/api/watch-progress')
      .then(async (records) => {
        const subjects = new Map<number, Promise<AnimeSubject>>();
        const subjectFor = (subjectId: number): Promise<AnimeSubject> => {
          const existing = subjects.get(subjectId);
          if (existing) {
            return existing;
          }
          const request = bgm<AnimeSubject>(`/v0/subjects/${subjectId}`);
          subjects.set(subjectId, request);
          return request;
        };
        const results = await Promise.allSettled(
          records.map(async (progress): Promise<ContinueWatchingItem> => ({
            progress,
            subject: await subjectFor(progress.bangumiSubjectId),
            episode: await bgm<Episode>(`/v0/episodes/${progress.bangumiEpisodeId}`),
          })),
        );
        if (!cancelled) {
          setItems(results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : [])));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setItems([]);
          setFailed(true);
          showToast(error instanceof Error ? error.message : String(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, bgm, retryRevision, showToast]);

  const removeProgress = async (episodeId: number): Promise<void> => {
    setDeletingEpisodeId(episodeId);
    try {
      await api(`/api/watch-progress/${episodeId}`, { method: 'DELETE' });
      setItems((current) => current.filter((item) => item.episode.id !== episodeId));
      showToast(t('watchProgressDeleted'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error), 'error');
    } finally {
      setDeletingEpisodeId(undefined);
    }
  };

  return (
    <section className="wall-section">
      <header>
        <div className="self-center">
          <h2>{t('watchHistory')}</h2>
        </div>
      </header>
      {loading ? (
        <LoadingWall />
      ) : failed ? (
        <RequestErrorWall onRetry={() => setRetryRevision((current) => current + 1)} />
      ) : items.length === 0 ? (
        <EmptyWall message={t('noContinueWatching')} />
      ) : (
        <div className="grid grid-cols-3 gap-3 max-[1050px]:grid-cols-2 max-[700px]:grid-cols-1">
          {items.map(({ episode, progress, subject }) => {
            const image = safeUrl(subject.images?.large ?? subject.images?.common ?? subject.images?.medium);
            const completed = progressDetails(progress.progress, progress.duration, 0.9).completed;
            const watchedAt = t('watchedAt').replace('{time}', formatDateTime(progress.updatedAt, locale));
            const search = new URLSearchParams({
              download: progress.downloadUuid,
              episode: String(episode.ep ?? episode.sort),
              episodeId: String(episode.id),
              subject: String(subject.id),
              fileIndex: String(progress.fileIndex),
            });
            if (!completed) {
              search.set('position', String(Math.floor(progress.progress)));
            }
            const href = `/playback?${search.toString()}`;
            return (
              <article
                className="continue-watching-item flex min-w-0 items-center gap-3 rounded-xl p-2 text-inherit no-underline transition-colors"
                key={episode.id}
              >
                <a
                  className="continue-watching-link flex min-w-0 flex-1 items-center gap-3 text-inherit no-underline"
                  href={href}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(href);
                  }}
                >
                  <span className="continue-watching-cover">
                    {image ? <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <Image />}
                    <span className="continue-watching-play absolute inset-0 grid place-items-center bg-black/30 opacity-0 transition-opacity">
                      <Play />
                    </span>
                  </span>
                  <span className="continue-watching-copy grid min-w-0 flex-1 gap-1.25">
                    <strong>{subject.name_cn || subject.name}</strong>
                    <small>
                      EP.{episode.sort}
                      {episode.name_cn || episode.name ? ` · ${episode.name_cn || episode.name}` : ''}
                    </small>
                    <ProgressBar
                      value={progress.progress}
                      max={progress.duration}
                      completionThreshold={0.9}
                      label={({ percentage }) =>
                        `${t('watchedPercent').replace('{percent}', String(percentage))} · ${watchedAt}`
                      }
                      completedLabel={`${t('finishedWatching')} · ${watchedAt}`}
                    />
                  </span>
                </a>
                <Button
                  iconOnly
                  color="danger"
                  className="shrink-0"
                  aria-label={t('deleteWatchProgress')}
                  title={t('deleteWatchProgress')}
                  disabled={deletingEpisodeId !== undefined}
                  loading={deletingEpisodeId === episode.id}
                  onClick={() => void removeProgress(episode.id)}
                >
                  <Trash2 className="size-4!" />
                </Button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

interface WatchLaterItem {
  episode: Episode;
  subject: AnimeSubject;
}

const WATCH_LATER_PAGE_SIZE = 12;

const WatchLaterSection = () => {
  const { api, bgm, navigate, showToast } = useApp();
  const [items, setItems] = useState<WatchLaterItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retryRevision, setRetryRevision] = useState(0);
  const [deletingEpisodeId, setDeletingEpisodeId] = useState<number>();
  const [mediaItem, setMediaItem] = useState<WatchLaterItem>();
  const subjects = useRef(new Map<number, Promise<AnimeSubject>>());

  const load = useCallback(
    async (nextOffset: number, append: boolean): Promise<void> => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setFailed(false);
      }
      try {
        const page = await api<WatchLaterPage>(`/api/watch-later?limit=${WATCH_LATER_PAGE_SIZE}&offset=${nextOffset}`);
        const subjectFor = (subjectId: number): Promise<AnimeSubject> => {
          const existing = subjects.current.get(subjectId);
          if (existing) {
            return existing;
          }
          const request = bgm<AnimeSubject>(`/v0/subjects/${subjectId}`);
          subjects.current.set(subjectId, request);
          return request;
        };
        const hydrated = await Promise.allSettled(
          page.data.map(async (record): Promise<WatchLaterItem> => ({
            subject: await subjectFor(record.bangumiSubjectId),
            episode: await bgm<Episode>(`/v0/episodes/${record.bangumiEpisodeId}`),
          })),
        );
        const nextItems = hydrated.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
        setItems((current) => (append ? [...current, ...nextItems] : nextItems));
        setOffset(nextOffset + page.data.length);
        setTotal(page.total);
      } catch (error) {
        if (!append) {
          setItems([]);
          setFailed(true);
        }
        showToast(error instanceof Error ? error.message : String(error), 'error');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [api, bgm, showToast],
  );

  useEffect(() => {
    subjects.current.clear();
    void load(0, false);
  }, [load, retryRevision]);

  const remove = async (episodeId: number): Promise<void> => {
    setDeletingEpisodeId(episodeId);
    try {
      await api(`/api/watch-later/${episodeId}`, { method: 'DELETE' });
      setItems((current) => current.filter((item) => item.episode.id !== episodeId));
      setTotal((current) => Math.max(0, current - 1));
      setOffset((current) => Math.max(0, current - 1));
      if (mediaItem?.episode.id === episodeId) {
        setMediaItem(undefined);
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error), 'error');
    } finally {
      setDeletingEpisodeId(undefined);
    }
  };

  return (
    <section className="wall-section">
      <header>
        <div className="self-center">
          <h2>{t('watchLater')}</h2>
        </div>
      </header>
      {loading ? (
        <LoadingWall />
      ) : failed ? (
        <RequestErrorWall onRetry={() => setRetryRevision((current) => current + 1)} />
      ) : items.length === 0 ? (
        <EmptyWall message={t('noWatchLater')} />
      ) : (
        <div className="grid grid-cols-3 gap-3 max-[1050px]:grid-cols-2 max-[700px]:grid-cols-1">
          {items.map((item) => {
            const { episode, subject } = item;
            const image = safeUrl(subject.images?.large ?? subject.images?.common ?? subject.images?.medium);
            const href = `/subjects/${subject.id}`;
            return (
              <article
                className="continue-watching-item flex min-w-0 items-center gap-3 rounded-xl p-2 text-inherit no-underline transition-colors"
                key={episode.id}
              >
                <a
                  className="continue-watching-link flex min-w-0 flex-1 items-center gap-3 text-inherit no-underline"
                  href={href}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(href);
                  }}
                >
                  <span className="continue-watching-cover">
                    {image ? <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <Image />}
                  </span>
                  <span className="continue-watching-copy grid min-w-0 flex-1 gap-1.25">
                    <strong>{subject.name_cn || subject.name}</strong>
                    <small>
                      EP.{episode.sort}
                      {episode.name_cn || episode.name ? ` · ${episode.name_cn || episode.name}` : ''}
                    </small>
                  </span>
                </a>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    iconOnly
                    aria-label={t('playMediaResource')}
                    title={t('playMediaResource')}
                    disabled={deletingEpisodeId !== undefined}
                    onClick={() => setMediaItem(item)}
                  >
                    <Play className="size-4!" />
                  </Button>
                  <Button
                    iconOnly
                    color="danger"
                    aria-label={t('removeWatchLater')}
                    title={t('removeWatchLater')}
                    disabled={deletingEpisodeId !== undefined}
                    loading={deletingEpisodeId === episode.id}
                    onClick={() => void remove(episode.id)}
                  >
                    <Trash2 className="size-4!" />
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {!loading && !failed && offset < total ? (
        <div className="mt-7 flex justify-center">
          <Button loading={loadingMore} className="min-h-9.5 px-4.5 font-[750]" onClick={() => void load(offset, true)}>
            <Plus />
            {t('loadMore')}
          </Button>
        </div>
      ) : null}
      {mediaItem ? (
        <MediaSourceDialog
          subject={mediaItem.subject}
          episode={mediaItem.episode}
          onCancel={() => setMediaItem(undefined)}
          onPlay={(result: MediaResourcePlayResult, episode) => {
            const search = new URLSearchParams({
              download: result.download.uuid,
              episode: String(episode.ep ?? episode.sort),
              episodeId: String(episode.id),
              subject: String(mediaItem.subject.id),
              watchLater: '1',
            });
            navigate(`/playback?${search.toString()}`);
          }}
        />
      ) : null}
    </section>
  );
};

interface TodayUpdate {
  subject: AnimeSubject;
  broadcastTime: string;
}

const TodayUpdatesSection = () => {
  const { api, bgm, bgmAuth, locale, navigate, showToast } = useApp();
  const currentUser = useStore($currentUser);
  const japaneseAnimeOnly = useStore($japaneseAnimeOnly);
  const showLateNightAnime = useStore($showLateNightAnime);
  const followingOnly = useStore($scheduleFollowingOnly);
  const [updates, setUpdates] = useState<TodayUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [followingIds, setFollowingIds] = useState<Set<number>>();
  const [followingLoading, setFollowingLoading] = useState(false);
  const [followingFailed, setFollowingFailed] = useState(false);
  const [retryRevision, setRetryRevision] = useState(0);
  const canFilterFollowing = Boolean(currentUser?.bgmToken);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void bgm<CalendarGroup[]>('/calendar')
      .then(async (groups) => {
        const subjectIds = groups.flatMap((group) => group.items.flatMap((item) => (item.id ? [item.id] : [])));
        const broadcasts = await api<SubjectBroadcast[]>('/api/schedule', {
          method: 'POST',
          body: JSON.stringify({ subjectIds }),
        });
        const broadcastsBySubject = new Map(broadcasts.map((broadcast) => [broadcast.subjectId, broadcast]));
        const formatter = new Intl.DateTimeFormat(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        });
        const subjectsForDay = (day: number): AnimeSubject[] => {
          const group = groups.find((item) => item.weekday.id === day);
          return (group?.items ?? [])
            .filter((item): item is typeof item & { id: number; name: string } => Boolean(item.id && item.name))
            .map((item) => ({ ...item, id: item.id, name: item.name }))
            .filter((subject) => !japaneseAnimeOnly || broadcastsBySubject.get(subject.id)?.language === 'ja');
        };
        const broadcastTimeFor = (subject: AnimeSubject): string => {
          const broadcastAt = broadcastsBySubject.get(subject.id)?.broadcastAt;
          const date = broadcastAt ? new Date(broadcastAt) : undefined;
          return date && !Number.isNaN(date.getTime()) ? formatter.format(date) : '';
        };
        const today = new Date().getDay() || 7;
        const nextDay = today === 7 ? 1 : today + 1;
        const nextUpdates: TodayUpdate[] = [];
        for (const subject of subjectsForDay(today)) {
          const broadcastTime = broadcastTimeFor(subject);
          const hour = Number(broadcastTime.split(':')[0]);
          if (!showLateNightAnime || !broadcastTime || hour >= 2) {
            nextUpdates.push({ subject, broadcastTime });
          }
        }
        if (showLateNightAnime) {
          for (const subject of subjectsForDay(nextDay)) {
            const broadcastTime = broadcastTimeFor(subject);
            const [hourText, minuteText] = broadcastTime.split(':');
            const hour = Number(hourText);
            if (broadcastTime && hour < 2) {
              nextUpdates.push({ subject, broadcastTime: `${hour + 24}:${minuteText}` });
            }
          }
        }
        const seen = new Set<number>();
        const sorted = nextUpdates
          .filter(({ subject }) => !seen.has(subject.id) && Boolean(seen.add(subject.id)))
          .sort((left, right) => {
            if (!left.broadcastTime) {
              return right.broadcastTime ? 1 : 0;
            }
            if (!right.broadcastTime) {
              return -1;
            }
            return (
              left.broadcastTime.localeCompare(right.broadcastTime) ||
              (left.subject.name_cn || left.subject.name).localeCompare(right.subject.name_cn || right.subject.name)
            );
          });
        if (!cancelled) {
          setUpdates(sorted);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setUpdates([]);
          setFailed(true);
          showToast(error instanceof Error ? error.message : String(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, bgm, japaneseAnimeOnly, locale, retryRevision, showLateNightAnime, showToast]);

  useEffect(() => {
    if (!followingOnly || !canFilterFollowing) {
      setFollowingIds(undefined);
      setFollowingLoading(false);
      setFollowingFailed(false);
      return;
    }
    let cancelled = false;
    setFollowingIds(undefined);
    setFollowingLoading(true);
    setFollowingFailed(false);
    void (async () => {
      const username = (await bgmAuth<BgmCurrentUser>('/v0/me')).username;
      const ids = new Set<number>();
      const limit = 100;
      let offset = 0;
      while (true) {
        const result = await bgmAuth<SubjectCollectionPage>(
          `/v0/users/${encodeURIComponent(username)}/collections?subject_type=2&type=3&limit=${limit}&offset=${offset}`,
        );
        for (const collection of result.data) {
          ids.add(collection.subject.id);
        }
        offset += result.data.length;
        if (result.data.length === 0 || offset >= result.total) {
          break;
        }
      }
      if (!cancelled) {
        setFollowingIds(ids);
      }
    })()
      .catch((error: unknown) => {
        if (!cancelled) {
          setFollowingFailed(true);
          showToast(error instanceof Error ? error.message : String(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFollowingLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [bgmAuth, canFilterFollowing, followingOnly, retryRevision, showToast]);

  const visibleUpdates = useMemo(
    () =>
      followingOnly && canFilterFollowing ? updates.filter(({ subject }) => followingIds?.has(subject.id)) : updates,
    [canFilterFollowing, followingIds, followingOnly, updates],
  );
  const sectionFailed = failed || followingFailed;
  const sectionLoading = loading || (followingOnly && canFilterFollowing && followingLoading);

  return (
    <section className="wall-section">
      <header>
        <div className="self-center">
          <h2>{t('todayUpdates')}</h2>
        </div>
        <div className="flex self-center flex-wrap items-center justify-end gap-3">
          {canFilterFollowing ? (
            <div className="flex items-center gap-2.5">
              <span className="whitespace-nowrap text-xs text-(--muted)">{t('scheduleFollowingOnly')}</span>
              <Switch label={t('scheduleFollowingOnly')} checked={followingOnly} onChange={setScheduleFollowingOnly} />
            </div>
          ) : null}
          <Button variant="text" onClick={() => navigate('/schedule')}>
            <CalendarDays />
            <span>{t('schedule')}</span>
          </Button>
        </div>
      </header>
      {sectionLoading ? (
        <LoadingWall />
      ) : sectionFailed ? (
        <RequestErrorWall onRetry={() => setRetryRevision((current) => current + 1)} />
      ) : visibleUpdates.length === 0 ? (
        <EmptyWall message={followingOnly && canFilterFollowing ? t('noContent') : undefined} />
      ) : (
        <div className="grid grid-cols-3 gap-x-6 gap-y-2 max-[1050px]:grid-cols-2 max-[700px]:grid-cols-1">
          {visibleUpdates.map(({ subject, broadcastTime }) => {
            const image = safeUrl(subject.images?.large ?? subject.images?.common ?? subject.images?.medium);
            return (
              <a
                className="today-update-item flex min-w-0 items-center gap-3 rounded-[10px] p-2 text-inherit no-underline transition-colors"
                href={`/subjects/${subject.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/subjects/${subject.id}`);
                }}
                key={subject.id}
              >
                <div className="today-update-cover">
                  {image ? <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <Image />}
                </div>
                <div className="today-update-copy grid min-w-0 flex-1 gap-1">
                  <time>
                    <Clock3 />
                    {broadcastTime || t('airTimeUnknown')}
                  </time>
                  <strong>{subject.name_cn || subject.name}</strong>
                  {subject.name_cn ? <small>{subject.name}</small> : null}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </section>
  );
};

const PopularSection = () => {
  const { bgm, navigate, showToast } = useApp();
  const showNsfw = useStore($showNsfw);
  const japaneseAnimeOnly = useStore($japaneseAnimeOnly);
  const [subjects, setSubjects] = useState<AnimeSubject[]>([]);
  const [index, setIndex] = useState(0);
  const [timerRevision, setTimerRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const swipeStart = useRef<{ x: number; y: number } | undefined>(undefined);
  const suppressClick = useRef(false);
  const initialSlideSelected = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const nextSubjects = await loadPopularSubjects(bgm, { japaneseAnimeOnly, showNsfw });
      const initialIndex = initialSlideSelected.current ? undefined : Math.floor(Math.random() * nextSubjects.length);
      if (nextSubjects.length > 0) {
        initialSlideSelected.current = true;
      }
      setSubjects(nextSubjects);
      setIndex((current) => initialIndex ?? (nextSubjects.length > 0 ? current % nextSubjects.length : 0));
    } catch (error) {
      setSubjects([]);
      setFailed(true);
      showToast(error instanceof Error ? error.message : String(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [bgm, japaneseAnimeOnly, showNsfw, showToast]);

  useEffect(() => void load(), [load]);
  useEffect(() => {
    if (subjects.length < 2) {
      return;
    }
    const timer = setTimeout(() => setIndex((current) => (current + 1) % subjects.length), 6000);
    return () => clearTimeout(timer);
  }, [index, subjects, timerRevision]);

  const select = (next: number): void => {
    if (subjects.length) {
      setIndex((next + subjects.length) % subjects.length);
      setTimerRevision((current) => current + 1);
    }
  };

  return (
    <section className="wall-section">
      <header>
        <div className="self-center">
          <h2>{t('popular')}</h2>
        </div>
      </header>
      <div
        className="popular-carousel"
        onPointerDown={(event) => {
          if (event.pointerType === 'touch') {
            swipeStart.current = { x: event.clientX, y: event.clientY };
          }
        }}
        onPointerUp={(event) => {
          const start = swipeStart.current;
          swipeStart.current = undefined;
          if (!start || event.pointerType !== 'touch') {
            return;
          }
          const distanceX = event.clientX - start.x;
          const distanceY = event.clientY - start.y;
          if (Math.abs(distanceX) >= 45 && Math.abs(distanceX) > Math.abs(distanceY)) {
            suppressClick.current = true;
            setTimeout(() => {
              suppressClick.current = false;
            }, 0);
            select(index + (distanceX < 0 ? 1 : -1));
          }
        }}
        onPointerCancel={() => {
          swipeStart.current = undefined;
        }}
        onClickCapture={(event) => {
          if (suppressClick.current) {
            suppressClick.current = false;
            event.preventDefault();
            event.stopPropagation();
          }
        }}
      >
        <div className="popular-carousel-viewport relative aspect-[2.33918/1] w-full overflow-hidden">
          <div className="popular-carousel-track">
            {loading ? (
              <LoadingWall />
            ) : failed ? (
              <RequestErrorWall onRetry={() => void load()} />
            ) : subjects.length ? (
              subjects.map((subject, subjectIndex) => {
                const previous = (index - 1 + subjects.length) % subjects.length;
                const next = (index + 1) % subjects.length;
                const position =
                  subjectIndex === index
                    ? 'active'
                    : subjectIndex === previous
                      ? 'previous'
                      : subjectIndex === next
                        ? 'next'
                        : '';
                return (
                  <PopularSlide
                    subject={subject}
                    position={position}
                    navigate={navigate}
                    onSelect={() => select(subjectIndex)}
                    key={subject.id}
                  />
                );
              })
            ) : (
              <EmptyWall />
            )}
          </div>
          {subjects.length > 1 && (
            <>
              <Button
                variant="plain"
                className="popular-fold-hit-area previous"
                aria-label="Previous slide"
                onClick={() => select(index - 1)}
              />
              <Button
                variant="plain"
                className="popular-fold-hit-area next"
                aria-label="Next slide"
                onClick={() => select(index + 1)}
              />
            </>
          )}
        </div>
        <div className="mt-3.5 flex min-h-2 justify-center gap-1.75">
          {subjects.map((subject, dot) => (
            <Button
              variant="plain"
              className={`popular-dot${dot === index ? ' active' : ''}`}
              onClick={() => select(dot)}
              aria-label={`Slide ${dot + 1}`}
              key={subject.id}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const PopularSlide = ({
  subject,
  position,
  navigate,
  onSelect,
}: {
  subject: AnimeSubject;
  position: string;
  navigate: (path: string) => void;
  onSelect: () => void;
}) => {
  const image = safeUrl(subject.images?.large ?? subject.images?.common ?? subject.images?.medium);
  const score = subject.score ?? subject.rating?.score;
  return (
    <a
      className={`popular-slide ${position}`.trim()}
      href={`/subjects/${subject.id}`}
      aria-hidden={!position}
      onClick={(event) => {
        event.preventDefault();
        if (position === 'active') {
          navigate(`/subjects/${subject.id}`);
        } else {
          onSelect();
        }
      }}
    >
      <div className="popular-backdrop">
        {image ? <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" /> : null}
      </div>
      <div className="popular-slide-content">
        <div>
          <h3>{subject.name_cn || subject.name}</h3>
          {subject.name_cn ? <p>{subject.name}</p> : null}
          <div className="popular-meta mt-5.5 flex items-center gap-3.5 text-[11px]">
            {score ? <strong>★ {score.toFixed(1)}</strong> : null}
            {subject.date ? <span>{subject.date}</span> : null}
          </div>
        </div>
        {image ? (
          <img className="popular-poster" src={image} alt="" loading="lazy" referrerPolicy="no-referrer" />
        ) : (
          <div className="popular-poster popular-poster-empty">
            <Image />
          </div>
        )}
      </div>
    </a>
  );
};

const collectionTabs: TabBarItem[] = [
  { value: 3, labelKey: 'collectionDoing' },
  { value: 1, labelKey: 'collectionWish' },
  { value: 2, labelKey: 'collectionDone' },
  { value: 4, labelKey: 'collectionOnHold' },
  { value: 5, labelKey: 'collectionDropped' },
  { value: 0, labelKey: 'collectionAll' },
];

const COLLECTION_PAGE_SIZE = 24;

interface CollectionCacheEntry {
  subjects: AnimeSubject[];
  total: number;
}

const CollectionSection = () => {
  const { bgmAuth, showToast } = useApp();
  const currentUser = useStore($currentUser);
  const [type, setType] = useState(3);
  const [subjects, setSubjects] = useState<AnimeSubject[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retryRevision, setRetryRevision] = useState(0);
  const cache = useRef(new Map<number, CollectionCacheEntry>());
  const username = useRef<string | undefined>(undefined);
  const requestRevision = useRef(0);

  useEffect(() => {
    requestRevision.current += 1;
    const cached = cache.current.get(type);
    if (cached) {
      setSubjects(cached.subjects);
      setTotal(cached.total);
      setFailed(false);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadingMore(false);
    setFailed(false);
    void (async () => {
      try {
        username.current ??= (await bgmAuth<BgmCurrentUser>('/v0/me')).username;
        const typeFilter = type ? `&type=${type}` : '';
        const result = await bgmAuth<SubjectCollectionPage>(
          `/v0/users/${encodeURIComponent(username.current)}/collections?subject_type=2${typeFilter}&limit=${COLLECTION_PAGE_SIZE}`,
        );
        if (!cancelled) {
          const next = result.data.map((collection) => collection.subject);
          cache.current.set(type, { subjects: next, total: result.total });
          setSubjects(next);
          setTotal(result.total);
        }
      } catch {
        if (!cancelled) {
          setSubjects([]);
          setTotal(0);
          setFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bgmAuth, retryRevision, type]);

  const loadMore = async (): Promise<void> => {
    if (loadingMore || subjects.length >= total || !username.current) {
      return;
    }
    const currentType = type;
    const currentRevision = requestRevision.current;
    setLoadingMore(true);
    try {
      const typeFilter = currentType ? `&type=${currentType}` : '';
      const result = await bgmAuth<SubjectCollectionPage>(
        `/v0/users/${encodeURIComponent(username.current)}/collections?subject_type=2${typeFilter}&limit=${COLLECTION_PAGE_SIZE}&offset=${subjects.length}`,
      );
      if (requestRevision.current !== currentRevision) {
        return;
      }
      const known = new Set(subjects.map((subject) => subject.id));
      const appended = result.data
        .map((collection) => collection.subject)
        .filter((subject) => !known.has(subject.id) && Boolean(known.add(subject.id)));
      const next = [...subjects, ...appended];
      const nextTotal = result.data.length > 0 ? result.total : next.length;
      cache.current.set(currentType, { subjects: next, total: nextTotal });
      setSubjects(next);
      setTotal(nextTotal);
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error), 'error');
    } finally {
      if (requestRevision.current === currentRevision) {
        setLoadingMore(false);
      }
    }
  };

  return (
    <section className="wall-section">
      <header>
        <div className="self-center">
          <h2>{t('myCollections')}</h2>
        </div>
      </header>
      <TabBar
        className="collection-tabs"
        items={collectionTabs}
        value={type}
        onChange={(value) => setType(Number(value))}
      />
      {loading ? (
        <LoadingWall />
      ) : failed ? (
        <RequestErrorWall
          message={currentUser?.bgmToken ? t('requestFailedRetryLater') : t('contentUnavailable')}
          onRetry={() => setRetryRevision((current) => current + 1)}
        />
      ) : (
        <AnimeWall subjects={subjects} emptyMessage={t('noContent')} />
      )}
      {!loading && !failed && subjects.length < total ? (
        <div className="mt-7 flex justify-center">
          <Button loading={loadingMore} className="min-h-9.5 px-4.5 font-[750]" onClick={() => void loadMore()}>
            <Plus />
            {t('loadMore')}
          </Button>
        </div>
      ) : null}
    </section>
  );
};

const RandomSection = () => {
  const { bgm, showToast } = useApp();
  const showNsfw = useStore($showNsfw);
  const japaneseAnimeOnly = useStore($japaneseAnimeOnly);
  const [subjects, setSubjects] = useState<AnimeSubject[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    const payload = {
      keyword: '',
      sort: 'heat',
      filter: {
        type: [2],
        ...(japaneseAnimeOnly ? { tag: ['日本'] } : {}),
        ...(!showNsfw ? { nsfw: false } : {}),
      },
    };
    const fetchPage = (limit: number, offset: number) =>
      bgm<SubjectPage>(`/v0/search/subjects?limit=${limit}&offset=${offset}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    try {
      const summary = await fetchPage(1, 0);
      const poolSize = Math.min(60, summary.total);
      if (poolSize === 0) {
        setSubjects([]);
        return;
      }
      const maxOffset = Math.max(0, summary.total - poolSize);
      const offset = Math.floor(Math.random() * (maxOffset + 1));
      const page = await fetchPage(poolSize, offset);
      setSubjects(
        [...page.data]
          .map((subject) => ({ subject, order: Math.random() }))
          .sort((left, right) => left.order - right.order)
          .slice(0, 12)
          .map(({ subject }) => subject),
      );
    } catch (error) {
      setSubjects([]);
      setFailed(true);
      showToast(error instanceof Error ? error.message : String(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [bgm, japaneseAnimeOnly, showNsfw, showToast]);

  useEffect(() => void load(), [load]);

  return (
    <section className="wall-section">
      <header>
        <div className="self-center">
          <h2>{t('randomAnime')}</h2>
        </div>
        <Button
          iconOnly
          className="self-center"
          loading={loading}
          aria-label={t('refresh')}
          onClick={() => void load()}
        >
          <RefreshCw />
        </Button>
      </header>
      {loading ? (
        <LoadingWall />
      ) : failed ? (
        <RequestErrorWall onRetry={() => void load()} />
      ) : subjects.length > 0 ? (
        <AnimeWall subjects={subjects} />
      ) : (
        <EmptyWall />
      )}
    </section>
  );
};

const RecommendationSection = () => {
  const { bgm, showToast } = useApp();
  const showNsfw = useStore($showNsfw);
  const japaneseAnimeOnly = useStore($japaneseAnimeOnly);
  const [catalog, setCatalog] = useState<AnimeSubject[]>([]);
  const [count, setCount] = useState(12);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retryRevision, setRetryRevision] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void (async () => {
      try {
        const size = 100;
        const payload = {
          keyword: '',
          sort: 'score',
          filter: {
            type: [2],
            rating: ['>7'],
            rating_count: ['>=200'],
            ...(japaneseAnimeOnly ? { tag: ['日本'] } : {}),
            ...(!showNsfw ? { nsfw: false } : {}),
          },
        };
        const fetchPage = (offset: number) =>
          bgm<SubjectPage>(`/v0/search/subjects?limit=${size}&offset=${offset}`, {
            method: 'POST',
            body: JSON.stringify(payload),
          });
        const first = await fetchPage(0);
        const pages = [first];
        const offsets = Array.from(
          { length: Math.max(0, Math.ceil(first.total / size) - 1) },
          (_, index) => (index + 1) * size,
        );
        for (let index = 0; index < offsets.length; index += 6) {
          pages.push(...(await Promise.all(offsets.slice(index, index + 6).map(fetchPage))));
        }
        const known = new Set<number>();
        const next = pages
          .flatMap((page) => page.data)
          .filter((subject) => !known.has(subject.id) && Boolean(known.add(subject.id)))
          .sort(
            (left, right) =>
              (Date.parse(right.date ?? '') || 0) - (Date.parse(left.date ?? '') || 0) || right.id - left.id,
          );
        if (!cancelled) {
          setCatalog(next);
          setCount(12);
        }
      } catch (error) {
        if (!cancelled) {
          setFailed(true);
        }
        showToast(error instanceof Error ? error.message : String(error), 'error');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bgm, japaneseAnimeOnly, retryRevision, showNsfw, showToast]);

  return (
    <section className="wall-section">
      <header>
        <div className="self-center">
          <h2>{t('recommendations')}</h2>
        </div>
      </header>
      {loading ? (
        <LoadingWall />
      ) : failed ? (
        <RequestErrorWall onRetry={() => setRetryRevision((current) => current + 1)} />
      ) : (
        <AnimeWall subjects={catalog.slice(0, count)} />
      )}
      {!loading && !failed && count < catalog.length && (
        <div className="mt-7 flex justify-center">
          <Button
            className="min-h-9.5 px-4.5 font-[750]"
            onClick={() => setCount((current) => Math.min(current + 12, catalog.length))}
          >
            <Plus />
            {t('loadMore')}
          </Button>
        </div>
      )}
    </section>
  );
};
